import PubSub from "pubsub-js"
import Layout from "./layout"
import type Component from "./shared/component.abstract"
import type { ConcreteComponentConstructor } from "./shared/component.abstract"
import ECommerce from "./shared/e-commerce/e-commerce"
import { scroller } from "./shared/helpers/scroller"

type ComponentDefinitions = { [identifier: string]: ConcreteComponentConstructor }

let registeredComponents: ComponentDefinitions = {}
const intialisedComponents: Component[] = []
let mountedHasBeenPublished = false

/**
 * Use when DOMContentLoaded
 * 
 * nojs-visibility classes is for fixing render when javascript is disabled
 * 
 * Remove classes nojs-visibility in an envir with js
 */
 const clearClassNoJSForSeo = () => {
    // Remove class in Js env
    document.querySelectorAll(".nojs-visibility").forEach(el => {
        el.classList.remove("nojs-visibility");
    });
}

export function start(components: ComponentDefinitions): void {
    registeredComponents = components
    document.addEventListener("DOMContentLoaded", async function (): Promise<void> {
        clearClassNoJSForSeo();
        scroller()
        new Layout()
        if (window.innerWidth > 767) {
            convertNativeSelectIntoJSSelect()
        }
        initialiseComponents()
        notifyMount()
    });
}

export function startLaStackMinimum(components: ComponentDefinitions): void {
    registeredComponents = components
    document.addEventListener("DOMContentLoaded", async function (): Promise<void> {
        scroller()
        new ECommerce()
        initialiseComponents()
        notifyMount()
    });
}

function convertNativeSelectIntoJSSelect(): void {
    document.querySelector("select.selectraw")?.classList.remove("selectraw")
}

const DO_NOT_USE_IDENTIFIER_SEPARATOR = "!DO_NOT_COPY_THIS!"
function parseIdentifier(identifier: string): {
    attributeName: string
    selector: string
} {
    let attributeSuffix, selector
    if (identifier.includes(DO_NOT_USE_IDENTIFIER_SEPARATOR)) {
        /*
        ⚠️ At the time of writing, only Selector needs this special override.
        We've done this for compatibility reasons when migrating it.
        Please do not use it for other components, we'd rather get rid of this than expand it.
         */
        [attributeSuffix, selector] = identifier.split(DO_NOT_USE_IDENTIFIER_SEPARATOR)
    } else {
        attributeSuffix = identifier
        selector = `[data-${identifier}]`
    }
    return {
        attributeName: `data-${attributeSuffix}`,
        selector: selector,
    }
}

export function initialiseComponents(): void {
    for (const [identifier, ComponentConstructor] of Object.entries(
        registeredComponents
    )) {
        const { attributeName, selector } = parseIdentifier(identifier)
        const elements: NodeListOf<HTMLElement> = document.querySelectorAll(selector)

        for (const element of elements) {
            initialiseComponentIfNeeded(ComponentConstructor, element, attributeName)
        }
    }
}

/**
 * Inform all components/classes that every instance has been created and is ready to work
 */
function notifyMount(): void {
    mountedHasBeenPublished = true
    PubSub.publish("mounted")
}

export function getAllInstancesByName(componentName: string): any[] {
    return intialisedComponents.filter(
        component => component.constructor.name === componentName
    )
}
export function getSingleInstanceByName(componentName: string): any {
    return intialisedComponents.find(
        component => component.constructor.name === componentName
    )
}

export function getAllInstancesByElement(
    componentName: string,
    elements: HTMLElement[]
): any {
    return elements.map(element => getSingleInstanceByElement(componentName, element))
}
export function getSingleInstanceByElement(
    componentName: string,
    element: HTMLElement
): any {
    return intialisedComponents.find(
        component =>
            component.constructor.name === componentName &&
            component.getElement() === element
    )
}

function _matchesComponentAndAttributeName(
    component: Component,
    componentName: string,
    nameSearched: string
): boolean {
    return (
        component.constructor.name === componentName &&
        component.getElement().getAttribute("name") === nameSearched
    )
}
export function getAllInstancesByElementName(
    componentName: string,
    nameSearched: string
): any[] {
    return intialisedComponents.filter(mountedComponent =>
        _matchesComponentAndAttributeName(mountedComponent, componentName, nameSearched)
    )
}
export function getInstanceByElementName(
    componentName: string,
    nameSearched: string
): any {
    return intialisedComponents.find(component =>
        _matchesComponentAndAttributeName(component, componentName, nameSearched)
    )
}

export function getAnyInstancesByElement(elementSearched: HTMLElement): Component[] {
    return intialisedComponents.filter(
        component => component.getElement() === elementSearched
    )
}

function initialiseComponentIfNeeded(
    ComponentConstructor: ConcreteComponentConstructor,
    element: HTMLElement,
    attributeName: string
): void {
    const existingInstance = getSingleInstanceByElement(ComponentConstructor.name, element);
    if (existingInstance) {
        if (existingInstance.reloadWorker) {
            existingInstance.reloadWorker();
        } 
        return;  
    }

    const component = new ComponentConstructor(
        _getProps(element, attributeName),
        element
    )
    intialisedComponents.push(component)

    if (mountedHasBeenPublished) {
        component.mounted()
    }
}

function _getProps(element: HTMLElement, attributeName: string) {
    const propsAttribute = element.getAttribute(attributeName)

    if (!!propsAttribute && propsAttribute !== "") {
        try {
            const jsronResult = JSON.parse(propsAttribute)
            if (typeof jsronResult === "number") {
                return { value: jsronResult }
            } else {
                return jsronResult
            }
        } catch (e) {
            return { value: propsAttribute }
        }
    }

    return {}
}
